import { Badge, Card, Group, Stack, Text } from '@mantine/core';
import {
  IconBook2,
  IconHeadphones,
  IconLink,
  IconFileTypePdf,
  IconPhoto,
  IconVideo,
} from '@tabler/icons-react';
import type { TrainingBlockType } from '@creativitas/shared';
import { AssetImage } from '@/components/ui/AssetImage';
import { LockedOverlay } from './LockedOverlay';

const BLOCK_ICON: Record<TrainingBlockType, typeof IconBook2> = {
  text: IconBook2,
  image: IconPhoto,
  video: IconVideo,
  audio: IconHeadphones,
  external_link: IconLink,
  pdf: IconFileTypePdf,
};

export type TrainingMaterialCardProps = {
  title: string;
  description?: string | null;
  coverImage?: string | null;
  /** Block types contained in the material — a hint of what is inside (§45). */
  blockTypes?: TrainingBlockType[];
  lockedReason?: string | null;
  /** Second line under the lock badge: the alternative route in (§19). */
  lockedHint?: string | null;
  onClick?: () => void;
};

export function TrainingMaterialCard({
  title,
  description,
  coverImage,
  blockTypes = [],
  lockedReason,
  lockedHint,
  onClick,
}: TrainingMaterialCardProps) {
  const uniqueTypes = [...new Set(blockTypes)];

  const body = (
    <Card
      withBorder
      padding="md"
      style={{
        borderColor: 'var(--border-subtle)',
        cursor: onClick && !lockedReason ? 'pointer' : 'default',
        height: '100%',
      }}
      onClick={lockedReason ? undefined : onClick}
    >
      <Card.Section>
        <AssetImage
          src={coverImage}
          alt={title}
          ratio="16 / 9"
          fallbackLabel="Обложка материала не загружена"
        />
      </Card.Section>

      <Stack gap={8} mt="md">
        <Text fw={600} size="lg" lh={1.3}>
          {title}
        </Text>

        {description ? (
          <Text size="sm" c="var(--text-muted)" lineClamp={2}>
            {description}
          </Text>
        ) : null}

        {uniqueTypes.length ? (
          <Group gap={6} mt={2}>
            {uniqueTypes.map((type) => {
              const Icon = BLOCK_ICON[type];
              return (
                <Badge key={type} size="xs" variant="default" leftSection={<Icon size={11} />}>
                  {type === 'text'
                    ? 'Текст'
                    : type === 'video'
                      ? 'Видео'
                      : type === 'audio'
                        ? 'Аудио'
                        : type === 'pdf'
                          ? 'PDF'
                          : 'Ссылка'}
                </Badge>
              );
            })}
          </Group>
        ) : null}
      </Stack>
    </Card>
  );

  return lockedReason ? (
    <LockedOverlay reason={lockedReason} hint={lockedHint ?? undefined}>
      {body}
    </LockedOverlay>
  ) : (
    body
  );
}
